import { isApiError } from '@multaqa/api-client'
import { useLocale, useTranslation } from '@multaqa/i18n'
import { ScreenSkeleton } from '@multaqa/ui/components/async/screen-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { PageHeader } from '@multaqa/ui/components/ui/page-header'
import {
  Section,
  SectionBody,
  SectionHeader,
  SectionTitle,
} from '@multaqa/ui/components/ui/section'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@multaqa/ui/components/ui/tabs'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BuildingIcon, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { InlineQueryError } from '@/components/inline-query-error'
import { localizedText, meQuery, useEffectiveGrants } from '@/lib/me'
import { usePaymentTermOptions } from '@/lib/payment-terms'
import {
  useAttachTag,
  useCustomer,
  useDetachTag,
  useReactivateCustomer,
  useRemoveContact,
  useTags,
} from '../hooks/use-customers'
import { CustomerInvoicesPanel, CustomerPaymentsPanel } from './customer-accounting-panels'
import { CustomerContactDialog } from './customer-contact-dialog'
import { CustomerDeactivateDialog } from './customer-deactivate-dialog'
import { CustomerDetailSections } from './customer-detail-sections'
import { CustomerFinancialSummaryStrip } from './customer-financial-summary-strip'
import { CustomerHeaderActions } from './customer-header-actions'
import { CustomerStatementPanel } from './customer-statement-panel'

const CUSTOMER_HUB_TABS = ['overview', 'invoices', 'payments', 'statement'] as const
export type CustomerHubTab = (typeof CUSTOMER_HUB_TABS)[number]

export function isCustomerHubTab(value: unknown): value is CustomerHubTab {
  return CUSTOMER_HUB_TABS.some((tab) => tab === value)
}

export type CustomerDetailProps = {
  customerId: string
  tab?: CustomerHubTab
  onTabChange?: (tab: CustomerHubTab) => void
  canViewInvoices?: boolean
  canViewPayments?: boolean
  canViewStatement?: boolean
}

export function CustomerDetail({
  customerId,
  tab,
  onTabChange,
  canViewInvoices = true,
  canViewPayments = true,
  canViewStatement = true,
}: CustomerDetailProps) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { can } = useEffectiveGrants()
  const { data: me } = useQuery(meQuery)
  const paymentTerms = usePaymentTermOptions(me?.active_company_id ?? null)
  const { data: customer, isLoading, isError, error, refetch } = useCustomer(customerId)
  const { data: tagsData } = useTags()
  const removeContact = useRemoveContact()
  const attachTag = useAttachTag()
  const detachTag = useDetachTag()
  const reactivate = useReactivateCustomer()

  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [selectedTagId, setSelectedTagId] = useState('')
  const [localTab, setLocalTab] = useState<CustomerHubTab>('overview')

  if (isLoading) {
    return <ScreenSkeleton className="min-h-full" />
  }

  const notFound = isApiError(error) && error.status === 404

  if (isError && !notFound) {
    return (
      <InlineQueryError
        title={t('common:error.asyncTitle')}
        description={t('common:error.asyncBody', { title: t('customer.title') })}
        retryLabel={t('common:action.retry')}
        onRetry={() => void refetch()}
      />
    )
  }

  if (notFound || !customer) {
    return (
      <EmptyState
        title={t('state.error')}
        description={t('error.notFound.title')}
        action={
          <Link to="/customers">
            <Button variant="outline">{t('customer.backToList')}</Button>
          </Link>
        }
      />
    )
  }

  const nameString =
    typeof customer.name === 'object' && customer.name
      ? localizedText(customer.name, locale) || customer.code
      : String(customer.name)
  const handleAttachTag = (tagId: string) => {
    if (!tagId) return
    attachTag.mutate({ customerId, tagId })
    setSelectedTagId('')
  }

  const attachedTagIds = new Set((customer.tags ?? []).map((tag) => tag.id))
  const availableTags = (tagsData ?? []).filter((tag) => !attachedTagIds.has(tag.id))
  const selectedTab = tab ?? localTab
  const activeTab = isTabAvailable(selectedTab, canViewInvoices, canViewPayments, canViewStatement)
    ? selectedTab
    : 'overview'
  const handleTabChange = (value: string) => {
    if (!isCustomerHubTab(value)) return
    if (!isTabAvailable(value, canViewInvoices, canViewPayments, canViewStatement)) return
    if (onTabChange) onTabChange(value)
    else setLocalTab(value)
  }

  return (
    <div className="space-y-10">
      <PageHeader
        trail={[
          { label: t('common:rail.company.sections.directory') },
          { label: t('customer.title'), href: '/customers' },
        ]}
        title={nameString}
        description={
          <span
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
            data-testid="customer-detail-meta"
          >
            <span>{t('customer.detailSubtitle', { code: customer.code })}</span>
            <Badge tone="neutral" data-testid="customer-detail-type">
              {customer.type === 'company' ? (
                <BuildingIcon className="me-1 inline size-3" />
              ) : (
                <UserIcon className="me-1 inline size-3" />
              )}
              {customer.type === 'company'
                ? t('customer.typeCompany')
                : t('customer.typeIndividual')}
            </Badge>
            <Badge
              tone={customer.is_active ? 'success' : 'danger'}
              data-testid="customer-detail-status"
            >
              {customer.is_active ? t('customer.active') : t('customer.inactive')}
            </Badge>
            {customer.is_intercompany ? (
              <Badge tone="warning">{t('customer.isIntercompany')}</Badge>
            ) : null}
          </span>
        }
        actions={
          <CustomerHeaderActions
            customerId={customerId}
            canEdit={can('customer.edit')}
            canDelete={can('customer.delete')}
            isActive={customer.is_active}
            activatePending={reactivate.isPending}
            onDeactivate={() => setDeactivateDialogOpen(true)}
            onActivate={() => reactivate.mutate(customerId)}
          />
        }
      />

      <CustomerFinancialSummaryStrip customerId={customerId} />

      <Tabs value={activeTab} onValueChange={handleTabChange} appearance="underline">
        <TabsList aria-label={t('customer.hub.tabsLabel')}>
          <TabsTrigger value="overview">{t('customer.hub.tabs.overview')}</TabsTrigger>
          {canViewInvoices ? (
            <TabsTrigger value="invoices">{t('customer.hub.tabs.invoices')}</TabsTrigger>
          ) : null}
          {canViewPayments ? (
            <TabsTrigger value="payments">{t('customer.hub.tabs.payments')}</TabsTrigger>
          ) : null}
          {canViewStatement ? (
            <TabsTrigger value="statement">{t('customer.hub.tabs.statement')}</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="overview">
          <CustomerDetailSections
            customer={customer}
            locale={locale}
            paymentTerms={paymentTerms.data ?? []}
            availableTags={availableTags}
            selectedTagId={selectedTagId}
            onAttachTag={handleAttachTag}
            onDetachTag={(tagId) => detachTag.mutate({ customerId, tagId })}
            onRemoveContact={(contactId) => removeContact.mutate({ customerId, contactId })}
            onAddContact={() => setContactDialogOpen(true)}
          />
        </TabsContent>
        {canViewInvoices ? (
          <TabsContent value="invoices">
            <Section>
              <SectionHeader>
                <SectionTitle>{t('customer.hub.tabs.invoices')}</SectionTitle>
              </SectionHeader>
              <SectionBody>
                <CustomerInvoicesPanel customerId={customerId} />
              </SectionBody>
            </Section>
          </TabsContent>
        ) : null}
        {canViewPayments ? (
          <TabsContent value="payments">
            <Section>
              <SectionHeader>
                <SectionTitle>{t('customer.hub.tabs.payments')}</SectionTitle>
              </SectionHeader>
              <SectionBody>
                <CustomerPaymentsPanel customerId={customerId} />
              </SectionBody>
            </Section>
          </TabsContent>
        ) : null}
        {canViewStatement ? (
          <TabsContent value="statement">
            <Section>
              <SectionHeader>
                <SectionTitle>{t('customer.hub.tabs.statement')}</SectionTitle>
              </SectionHeader>
              <SectionBody>
                <CustomerStatementPanel customerId={customerId} />
              </SectionBody>
            </Section>
          </TabsContent>
        ) : null}
      </Tabs>

      <CustomerContactDialog
        customerId={customerId}
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
      />

      <CustomerDeactivateDialog
        customerId={customerId}
        customerCode={customer.code}
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
      />
    </div>
  )
}

function isTabAvailable(
  tab: CustomerHubTab,
  canViewInvoices: boolean,
  canViewPayments: boolean,
  canViewStatement: boolean,
) {
  if (tab === 'invoices') return canViewInvoices
  if (tab === 'payments') return canViewPayments
  if (tab === 'statement') return canViewStatement
  return true
}
